/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, type ReactNode, useCallback, useEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Stack } from '@atlaskit/primitives/compiled';
import Tabs, { TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { CollapsibleItem } from '../../collapsible-item';
import { PillTab } from '../pill-tab';

const tabsListStyles = css({
	paddingBottom: token('space.250'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'div[role=tablist]': {
		marginInlineStart: 'unset',
		gap: token('space.050'),
		'&::before': {
			insetInlineStart: 'unset',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
		'[role=tab]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
			'&[aria-selected=true]::after': {
				insetInlineStart: 'unset',
			},
			'&:hover::after': {
				insetInlineStart: 'unset',
			},
		},
	},
});

type Sections<TSection> = {
	component: ReactNode;
	defaultIsExpanded?: boolean;
	sectionId: TSection;
	title?: string;
};

export type TabProps<TSection> = {
	label: string;
	tabContent: Sections<TSection>[];
	value: string;
};

type ConfigPanelGroupingProps<TSection> = {
	onTabClick?: (tab: TabProps<TSection>) => void;
	source: string;
	tabSections: TabProps<TSection>[];
};

export const ConfigPanelGrouping = <TSection extends string>({
	source,
	tabSections,
	onTabClick,
}: ConfigPanelGroupingProps<TSection>): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleTabClick = useCallback(
		(index: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'tab',
					actionSubjectId: 'configPanelNavigationPills',
					source,
					attributes: {
						selectedValue: tabSections[index].value,
					},
				},
			}).fire();

			onTabClick?.(tabSections[index]);
		},
		[createAnalyticsEvent, tabSections, source, onTabClick],
	);

	const configPanelRef = useRef<HTMLDivElement>(null);
	// Focus the first tab when the config panel opens
	useEffect(() => {
		if (configPanelRef.current) {
			const firstTab = configPanelRef.current.querySelector('[role="tab"]');
			if (firstTab) {
				(firstTab as HTMLElement).focus();
			}
		}
	}, []);

	return (
		<Tabs id={`${source}-config-panel-tabs`} onChange={handleTabClick}>
			<div ref={configPanelRef} css={tabsListStyles}>
				<TabList>
					{tabSections.map((tab, index) => {
						return <PillTab key={index} label={tab.label} />;
					})}
				</TabList>
			</div>
			{tabSections.map((tab, index) => {
				return (
					<TabPanel key={index}>
						<Stack grow="fill" testId="tab-panel-content">
							{tab.tabContent.map((section) =>
								section.defaultIsExpanded !== undefined && section.title ? (
									<CollapsibleItem
										key={section.sectionId}
										title={section.title}
										section={section.sectionId}
										defaultIsExpanded={section?.defaultIsExpanded}
										source={source}
									>
										{section.component}
									</CollapsibleItem>
								) : (
									<Fragment key={section.sectionId}>{section.component}</Fragment>
								),
							)}
						</Stack>
					</TabPanel>
				);
			})}
		</Tabs>
	);
};
